import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useTranslation } from 'react-i18next';
import '@/i18n';
import { getLanguageForCountry, changeLanguage } from '@/i18n';

// Import utilities
import api from "@/utils/api";
import { fetchLiveRates } from "@/utils/constants";

// Import components
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Lazy load heavy components
const GlobalMap = lazy(() => import("@/components/GlobalMap"));
const BulkListingPage = lazy(() => import("@/components/BulkListingPage"));

// Import pages
import { 
  HomePage, 
  BrowsePage, 
  ItemDetailPage, 
  SellPage,
  DashboardPage,
  ProfilePage,
  OrdersPage,
  MessagesPage,
  MapPage,
  AdminDashboard,
  EditListingPage,
  AuthCallbackPage,
  VerifyEmailPage,
  ResetPasswordPage,
  PaymentSuccessPage,
  BidZuuLivePage,
  StreamWatchPage,
  LandingAuthPage,
  AccountPage,
  MyShowsPage,
  CartPage
} from "@/pages";

// Import legal pages and other components
import { TermsOfService, PrivacyPolicy } from "@/components/LegalPages";
import { SellerTaxInfoPage } from "@/components/SellerTaxInfo";
import DeliveryTrackingPage from "@/pages/DeliveryTrackingPage";
import MyDeliveriesPage from "@/pages/MyDeliveriesPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import CommunityPage from "@/pages/CommunityPage";
import CommunityPostDetail from "@/pages/CommunityPostDetail";
import GroupsPage from "@/pages/GroupsPage";
import CreateGroupPage from "@/pages/CreateGroupPage";
import GroupDetailPage from "@/pages/GroupDetailPage";
import GroupSettingsPage from "@/pages/GroupSettingsPage";
import InspectorsPage from "@/pages/InspectorsPage";
import ImportFromEbay from "@/pages/ImportFromEbay";
import AIChatBubble from "@/components/AIChatBubble";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import OrderPaymentPage from "@/pages/OrderPaymentPage";
import ReferAndEarnPage from "@/pages/ReferAndEarnPage";

// Loading spinner component for lazy loaded components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
  </div>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Auth Hook
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch {
      setUser(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, token) => {
    setUser(userData);
    if (token) localStorage.setItem("token", token);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore logout errors */ }
    localStorage.removeItem("token");
    localStorage.removeItem("userCurrency");
    localStorage.removeItem("userCurrencyTime");
    setUser(null);
    window.location.href = '/';
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return { user, loading, login, logout, checkAuth };
};

// App Router
function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Set document title based on page
  useEffect(() => {
    const pageTitles = {
      '/': 'BidZuu - Buy, Sell & Bid on Amazing Items',
      '/browse': 'Browse Items | BidZuu',
      '/live': 'BidZuu Live - Watch & Shop Live Streams',
      '/help': 'Help Center | BidZuu',
      '/dashboard': 'My Dashboard | BidZuu',
      '/sell': 'Sell Your Items | BidZuu',
      '/orders': 'My Orders | BidZuu',
      '/messages': 'Messages | BidZuu',
      '/settings': 'Settings | BidZuu',
    };
    
    const path = location.pathname;
    document.title = pageTitles[path] || 'BidZuu - Buy, Sell & Bid on Amazing Items';
  }, [location.pathname]);
  
  // Check for verification token in URL - redirect to verify page if found anywhere
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    
    // If there's a token and we're not already on verify-email or reset-password page
    if (token && !location.pathname.includes('verify-email') && !location.pathname.includes('reset-password')) {
      // Check if this looks like a verification token (not a reset token)
      // Reset tokens typically come with the /reset-password path
      console.log('Token found in URL, redirecting to verify-email page');
      navigate(`/verify-email?token=${token}`, { replace: true });
    }
  }, [location, navigate]);
  
  // Check for session_id in URL hash (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return <AppContent />;
}

// Main App Content
function AppContent() {
  const { user, loading, logout, login } = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const [userCurrency, setUserCurrency] = useState({ code: 'USD', symbol: '$', country: 'US' });
  
  // Pages where footer should not show
  const noFooterPages = ['/map', '/admin', '/live'];
  const showFooter = !noFooterPages.some(page => location.pathname === page || location.pathname.startsWith(page + '/'));
  
  // Pages where header should not show
  const noHeaderPages = ['/admin', '/live', '/my-shows'];
  const showHeader = !noHeaderPages.some(page => location.pathname === page || location.pathname.startsWith(page + '/'));

  // Fetch live exchange rates on app load
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetchLiveRates(API_URL).then(rates => {
      if (rates) {
        console.log('Live exchange rates loaded');
      }
    });
  }, []);

  // Load user's saved currency preference if logged in
  useEffect(() => {
    const loadUserCurrencyPreference = async () => {
      if (user) {
        try {
          const response = await api.get('/users/currency-preference');
          if (response.data.currency_code) {
            const savedCurrency = {
              code: response.data.currency_code,
              symbol: response.data.currency_symbol || '$',
              country: userCurrency?.country || 'US'
            };
            // Fetch the symbol from our API
            const currencyInfo = await api.get(`/currency/info?country_code=${savedCurrency.country}`);
            if (currencyInfo.data.currency_code === response.data.currency_code) {
              savedCurrency.symbol = currencyInfo.data.currency_symbol;
            }
            setUserCurrency(savedCurrency);
            localStorage.setItem('userCurrency', JSON.stringify(savedCurrency));
            localStorage.setItem('userCurrencyTime', Date.now().toString());
          }
        } catch (error) {
          // User hasn't set a preference, use auto-detected
        }
      }
    };
    loadUserCurrencyPreference();
  }, [user]);

  // Detect user's country and set currency + language
  useEffect(() => {
    const detectUserLocale = async () => {
      try {
        const stored = localStorage.getItem('userCurrency');
        const storedTime = localStorage.getItem('userCurrencyTime');
        const storedLang = localStorage.getItem('i18nextLng');
        const oneDay = 24 * 60 * 60 * 1000;
        
        if (stored && storedTime && (Date.now() - parseInt(storedTime)) < oneDay) {
          setUserCurrency(JSON.parse(stored));
          return;
        }
        
        // Try multiple geolocation services with fallbacks
        let countryCode = 'US';
        const geoServices = [
          { url: 'https://ipapi.co/json/', parser: (d) => d.country_code },
          { url: 'https://ip-api.com/json/', parser: (d) => d.countryCode },
          { url: 'https://ipinfo.io/json', parser: (d) => d.country },
          { url: 'https://api.country.is/', parser: (d) => d.country }
        ];
        
        for (const service of geoServices) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000);
            
            const geoRes = await fetch(service.url, { 
              signal: controller.signal,
              mode: 'cors'
            });
            clearTimeout(timeoutId);
            
            if (geoRes.ok) {
              const geoData = await geoRes.json();
              const detected = service.parser(geoData);
              if (detected && detected.length === 2) {
                countryCode = detected.toUpperCase();
                console.log(`Detected country: ${countryCode} from ${service.url}`);
                break;
              }
            }
          } catch (geoError) {
            // Try next service
            continue;
          }
        }
        
        // If all services fail, try to use browser's timezone to guess country
        if (countryCode === 'US') {
          try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const timezoneCountries = {
              'Asia/Karachi': 'PK', 'Asia/Kolkata': 'IN', 'Asia/Tokyo': 'JP',
              'Asia/Shanghai': 'CN', 'Asia/Hong_Kong': 'HK', 'Asia/Singapore': 'SG',
              'Asia/Bangkok': 'TH', 'Asia/Jakarta': 'ID', 'Asia/Manila': 'PH',
              'Asia/Seoul': 'KR', 'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA',
              'Europe/London': 'GB', 'Europe/Paris': 'FR', 'Europe/Berlin': 'DE',
              'Europe/Rome': 'IT', 'Europe/Madrid': 'ES', 'Europe/Moscow': 'RU',
              'Australia/Sydney': 'AU', 'Pacific/Auckland': 'NZ',
              'America/Toronto': 'CA', 'America/Mexico_City': 'MX',
              'America/Sao_Paulo': 'BR', 'America/Buenos_Aires': 'AR',
              'Africa/Lagos': 'NG', 'Africa/Cairo': 'EG', 'Africa/Johannesburg': 'ZA',
              'Africa/Nairobi': 'KE'
            };
            if (timezoneCountries[timezone]) {
              countryCode = timezoneCountries[timezone];
              console.log(`Detected country from timezone ${timezone}: ${countryCode}`);
            }
          } catch (e) {
            // Ignore timezone detection errors
          }
        }
        
        const currencyRes = await api.get(`/currency/info?country_code=${countryCode}`);
        const newCurrency = {
          code: currencyRes.data.currency_code,
          symbol: currencyRes.data.currency_symbol,
          country: countryCode
        };
        setUserCurrency(newCurrency);
        localStorage.setItem('userCurrency', JSON.stringify(newCurrency));
        localStorage.setItem('userCurrencyTime', Date.now().toString());
        
        if (!storedLang) {
          const detectedLang = getLanguageForCountry(countryCode);
          if (detectedLang && detectedLang !== i18n.language) {
            changeLanguage(detectedLang);
          }
        }
      } catch (error) {
        console.log('Using default USD currency and English language');
      }
    };
    
    detectUserLocale();
  }, [i18n]);

  if (loading) {
    return <PageLoader />;
  }

  // Guest-accessible routes (can view without login)
  const guestAccessibleRoutes = ['/', '/browse', '/item', '/map', '/terms', '/privacy', '/auth', '/verify-email', '/reset-password', '/live', '/help', '/community', '/messages', '/inspectors'];
  const isGuestAccessible = guestAccessibleRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(route + '/')
  );

  // Show landing auth page only for protected routes when not logged in
  if (!user && !isGuestAccessible && !location.pathname.startsWith('/admin')) {
    return <LandingAuthPage onLogin={login} />;
  }

  return (
    <>
      {showHeader && <Header user={user} onLogout={logout} userCurrency={userCurrency} setUserCurrency={setUserCurrency} />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage user={user} userCurrency={userCurrency} />} />
          <Route path="/browse" element={<BrowsePage userCurrency={userCurrency} />} />
          <Route path="/map" element={<MapPage userCurrency={userCurrency} />} />
          <Route path="/item/:id" element={<ItemDetailPage user={user} userCurrency={userCurrency} />} />
          <Route path="/item/:listingId/edit" element={<EditListingPage user={user} />} />
          <Route path="/sell" element={<SellPage user={user} />} />
          <Route path="/sell/bulk" element={<BulkListingPage user={user} api={api} />} />
          <Route path="/seller/tax-info" element={<SellerTaxInfoPage user={user} />} />
          <Route path="/account" element={<AccountPage user={user} onLogout={logout} userCurrency={userCurrency} setUserCurrency={setUserCurrency} />} />
          <Route path="/dashboard" element={<DashboardPage user={user} userCurrency={userCurrency} />} />
          <Route path="/orders" element={<OrdersPage user={user} userCurrency={userCurrency} />} />
          <Route path="/orders/:orderId" element={<OrderPaymentPage user={user} />} />
          <Route path="/orders/:orderId/success" element={<OrderPaymentPage user={user} />} />
          <Route path="/orders/:orderId/paypal-success" element={<OrderPaymentPage user={user} />} />
          <Route path="/refer" element={<ReferAndEarnPage />} />
          <Route path="/refer-earn" element={<ReferAndEarnPage />} />
          <Route path="/profile/:userId" element={<ProfilePage currentUser={user} userCurrency={userCurrency} />} />
          <Route path="/messages" element={<MessagesPage user={user} />} />
          <Route path="/live" element={<BidZuuLivePage user={user} />} />
          <Route path="/live/:streamId" element={<StreamWatchPage user={user} />} />
          <Route path="/my-shows" element={<MyShowsPage user={user} />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/auth/google/callback" element={<AuthCallbackPage />} />
          <Route path="/auth/facebook/callback" element={<AuthCallbackPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth" element={<LandingAuthPage onLogin={login} />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/delivery/track/:deliveryId" element={<DeliveryTrackingPage />} />
          <Route path="/my-deliveries" element={<MyDeliveriesPage user={user} />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/community/post/:postId" element={<CommunityPostDetail />} />
          <Route path="/community/groups" element={<GroupsPage />} />
          <Route path="/community/groups/create" element={<CreateGroupPage />} />
          <Route path="/community/groups/:groupId" element={<GroupDetailPage />} />
          <Route path="/community/groups/:groupId/settings" element={<GroupSettingsPage />} />
          <Route path="/inspectors" element={<InspectorsPage user={user} />} />
          <Route path="/import/ebay" element={<ImportFromEbay user={user} />} />
          <Route path="/cart" element={<CartPage user={user} userCurrency={userCurrency} />} />
        </Routes>
      </Suspense>
      {showFooter && <Footer />}
      <AIChatBubble />
      <AnalyticsTracker />
    </>
  );
}

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-center" richColors />
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
